import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useEmpFeatureFlag } from '@/hooks/useEmpFeatureFlag';
import { useAuth } from '@/contexts/AuthContext';

export interface PortfolioKPIs {
  spiMedio: number | null;
  cpiMedio: number | null;
  progressoFisico: number | null;
  progressoPrevisto: number | null;
  desviosAbertos: number;
  alertasCriticos: number;
}

export interface PortfolioContractRow {
  id: string;
  nome: string;
  status: string;
  valorGlobal: number;
  spi: number | null;
  cpi: number | null;
  progressoRealizado: number | null;
  progressoPrevisto: number | null;
  desvios: number;
  alertas: number;
  criticidade: 'critico' | 'alerta' | 'ok';
}

export interface PortfolioTrendPoint {
  periodo: string;
  spi: number | null;
  cpi: number | null;
}

function calcCriticidade(row: Omit<PortfolioContractRow, 'criticidade'>): 'critico' | 'alerta' | 'ok' {
  const spi = row.spi ?? 1;
  const cpi = row.cpi ?? 1;
  if (spi < 0.80 || cpi < 0.80 || row.desvios >= 3) return 'critico';
  if (spi < 0.95 || row.alertas >= 2) return 'alerta';
  return 'ok';
}

export function usePortfolioDashboard(selectedClusterIds: string[]) {
  const { activeClusterIds } = useEmpFeatureFlag();
  const { role, gestorClusterIds } = useAuth();

  // Scope: use selected (if provided) else all active clusters (respecting role)
  const scopeClusterIds = (() => {
    const base =
      role === 'gestor'
        ? activeClusterIds.filter(id => gestorClusterIds.includes(id))
        : activeClusterIds;
    if (selectedClusterIds.length > 0) {
      return base.filter(id => selectedClusterIds.includes(id));
    }
    return base;
  })();

  const { data: contratos = [], isLoading: loadingContratos } = useQuery({
    queryKey: ['portfolio-contratos', scopeClusterIds],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('emp_contratos')
        .select('id, nome, status, valor_global, cluster_id')
        .in('cluster_id', scopeClusterIds);
      if (error) throw error;
      return data || [];
    },
    enabled: scopeClusterIds.length > 0,
    staleTime: 2 * 60 * 1000,
  });

  const contratoIds = contratos.map(c => c.id);

  const { data: indicadores = [], isLoading: loadingIndicadores } = useQuery({
    queryKey: ['portfolio-indicadores', contratoIds],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('emp_vw_indicadores_blindados' as any)
        .select('contrato_id, periodo_referencia, spi, cpi, percentual_fisico_previsto, percentual_fisico_realizado')
        .in('contrato_id', contratoIds)
        .order('periodo_referencia', { ascending: true }) as any;
      if (error) throw error;
      return data || [];
    },
    enabled: contratoIds.length > 0,
    staleTime: 2 * 60 * 1000,
  });

  const { data: desvios = [], isLoading: loadingDesvios } = useQuery({
    queryKey: ['portfolio-desvios', contratoIds],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('emp_desvios')
        .select('id, contrato_id')
        .in('contrato_id', contratoIds)
        .eq('status', 'aberto');
      if (error) throw error;
      return data || [];
    },
    enabled: contratoIds.length > 0,
    staleTime: 2 * 60 * 1000,
  });

  const { data: alertas = [], isLoading: loadingAlertas } = useQuery({
    queryKey: ['portfolio-alertas', contratoIds],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('emp_alertas')
        .select('id, contrato_id, severidade')
        .in('contrato_id', contratoIds)
        .in('status', ['aberto', 'reconhecido']);
      if (error) throw error;
      return data || [];
    },
    enabled: contratoIds.length > 0,
    staleTime: 2 * 60 * 1000,
  });

  const isLoading = loadingContratos || loadingIndicadores || loadingDesvios || loadingAlertas;

  // --- Derived: latest indicator per contract ---
  const latestByContrato = new Map<string, typeof indicadores[0]>();
  indicadores.forEach(ind => {
    const existing = latestByContrato.get(ind.contrato_id);
    if (!existing || ind.periodo_referencia > existing.periodo_referencia) {
      latestByContrato.set(ind.contrato_id, ind);
    }
  });

  // --- Derived: counts per contract ---
  const desviosByContrato = desvios.reduce((acc, d) => {
    acc[d.contrato_id] = (acc[d.contrato_id] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const alertasCriticosByContrato = alertas.reduce((acc, a) => {
    if (['critica', 'alta'].includes(a.severidade)) {
      acc[a.contrato_id] = (acc[a.contrato_id] || 0) + 1;
    }
    return acc;
  }, {} as Record<string, number>);

  // --- KPIs (S2: weighted average excluding NULLs) ---
  const latestList = Array.from(latestByContrato.values());

  // SPI: weighted by baseline_bac, excluding NULLs
  const spiWeighted = latestList
    .filter(i => i.spi != null && i.baseline_bac != null && Number(i.baseline_bac) > 0)
    .reduce((acc, i) => {
      const bac = Number(i.baseline_bac);
      acc.totalPeso += bac;
      acc.totalValor += (i.spi as number) * bac;
      return acc;
    }, { totalPeso: 0, totalValor: 0 });

  // CPI: weighted by baseline_bac, excluding NULLs
  const cpiWeighted = latestList
    .filter(i => i.cpi != null && i.baseline_bac != null && Number(i.baseline_bac) > 0)
    .reduce((acc, i) => {
      const bac = Number(i.baseline_bac);
      acc.totalPeso += bac;
      acc.totalValor += (i.cpi as number) * bac;
      return acc;
    }, { totalPeso: 0, totalValor: 0 });

  const progRealizadoValues = latestList.map(i => i.percentual_fisico_realizado).filter((v): v is number => v !== null);
  const progPrevistoValues = latestList.map(i => i.percentual_fisico_previsto).filter((v): v is number => v !== null);

  const avg = (arr: number[]) => arr.length > 0 ? arr.reduce((s, v) => s + v, 0) / arr.length : null;

  const kpis: PortfolioKPIs = {
    spiMedio: spiWeighted.totalPeso > 0 ? spiWeighted.totalValor / spiWeighted.totalPeso : null,
    cpiMedio: cpiWeighted.totalPeso > 0 ? cpiWeighted.totalValor / cpiWeighted.totalPeso : null,
    progressoFisico: avg(progRealizadoValues),
    progressoPrevisto: avg(progPrevistoValues),
    desviosAbertos: desvios.length,
    alertasCriticos: alertas.filter(a => ['critica', 'alta'].includes(a.severidade)).length,
  };

  // --- Contract ranking ---
  const ranking: PortfolioContractRow[] = contratos.map(c => {
    const ind = latestByContrato.get(c.id);
    const row = {
      id: c.id,
      nome: c.nome || 'Sem nome',
      status: c.status,
      valorGlobal: c.valor_global || 0,
      spi: ind?.spi ?? null,
      cpi: ind?.cpi ?? null,
      progressoRealizado: ind?.percentual_fisico_realizado ?? null,
      progressoPrevisto: ind?.percentual_fisico_previsto ?? null,
      desvios: desviosByContrato[c.id] || 0,
      alertas: alertasCriticosByContrato[c.id] || 0,
    };
    return { ...row, criticidade: calcCriticidade(row) };
  }).sort((a, b) => {
    const order = { critico: 0, alerta: 1, ok: 2 };
    return order[a.criticidade] - order[b.criticidade];
  });

  // --- Trend data: avg SPI/CPI across contracts per period (excluding NULLs) ---
  const periodMap = new Map<string, { spiSum: number; spiCount: number; cpiSum: number; cpiCount: number }>();
  indicadores.forEach(ind => {
    const p = (ind.periodo_referencia || '').slice(0, 7);
    if (!p) return;
    const existing = periodMap.get(p) || { spiSum: 0, spiCount: 0, cpiSum: 0, cpiCount: 0 };
    periodMap.set(p, {
      spiSum: existing.spiSum + (ind.spi != null ? Number(ind.spi) : 0),
      spiCount: existing.spiCount + (ind.spi != null ? 1 : 0),
      cpiSum: existing.cpiSum + (ind.cpi != null ? Number(ind.cpi) : 0),
      cpiCount: existing.cpiCount + (ind.cpi != null ? 1 : 0),
    });
  });

  const trend: PortfolioTrendPoint[] = Array.from(periodMap.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([periodo, { spiSum, spiCount, cpiSum, cpiCount }]) => ({
      periodo,
      spi: spiCount > 0 ? Math.round((spiSum / spiCount) * 100) / 100 : null,
      cpi: cpiCount > 0 ? Math.round((cpiSum / cpiCount) * 100) / 100 : null,
    }));

  return {
    isLoading,
    scopeClusterIds,
    contratos,
    kpis,
    ranking,
    trend,
    hasData: contratos.length > 0,
  };
}
